/*
 * yumaté motion layer.
 * - Reveals content as it scrolls into view (only elements below the fold at load, so nothing
 *   above the fold flashes; without JS everything stays visible). A scroll sweep catches
 *   anything skipped by fast jumps, so no heading ever stays hidden.
 * - Counts numbers up once when they enter the viewport.
 * - Shows the floating pill while hero, buy box and footer are out of view.
 * - Lets the ritual photo settle when it enters the viewport.
 * - Adds the homepage buy box to the cart without leaving the page and opens the cart drawer.
 * Everything respects prefers-reduced-motion.
 */
(function () {
  if (window.ymMotionInit) return;
  window.ymMotionInit = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('ym-js');

  var REVEAL = [
    '.ym-head',
    '.ym-benefit',
    '.ym-story__media',
    '.ym-story__copy',
    '.ym-step',
    '.ym-chips',
    '.ym-buy__media',
    '.ym-buy__copy',
    '.ym-faq__intro',
    '.ym-faq__item',
    '.ym-nutri__card',
    '.ym-nutri__info',
    '.ym-ritual__copy',
    '.ym-stat',
    '.ym-compare__wrap',
    '.ym-footbrand .ym-wrap > *'
  ].join(',');

  var pending = 0;
  var sweepQueued = false;

  function reveal(el) {
    if (el.classList.contains('is-in')) return;
    el.classList.add('is-in');
    pending = Math.max(0, pending - 1);
  }

  /* Reveal everything whose top has already passed the bottom of the viewport. */
  function sweep() {
    sweepQueued = false;
    if (!pending) return;
    var limit = window.innerHeight * 0.95;
    document.querySelectorAll('.ym-reveal-ready:not(.is-in)').forEach(function (el) {
      if (el.getBoundingClientRect().top < limit) reveal(el);
    });
  }

  function queueSweep() {
    if (sweepQueued || !pending) return;
    sweepQueued = true;
    requestAnimationFrame(sweep);
  }

  function setup(scope) {
    scope = scope || document;
    var els = scope.querySelectorAll(REVEAL);
    var counters = scope.querySelectorAll('[data-ym-count]');
    var zooms = scope.querySelectorAll('.ym-ritual');

    if (reduce || !('IntersectionObserver' in window)) return;

    var vh = window.innerHeight;
    var narrow = window.innerWidth < 750;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });

    els.forEach(function (el) {
      if (el.classList.contains('ym-reveal-ready')) return;
      if (el.getBoundingClientRect().top < vh * 0.92) return;
      /* Cards in the swipe row on phones stay visible; hiding them would leave gaps while swiping. */
      if (narrow && el.closest('.ym-benefits__grid')) return;
      var parent = el.parentElement;
      var index = 0;
      if (parent) {
        var group = Array.prototype.filter.call(parent.children, function (c) { return c.matches(REVEAL); });
        index = Math.max(0, group.indexOf(el));
      }
      el.style.setProperty('--ym-i', Math.min(index, 5));
      el.classList.add('ym-reveal-ready');
      pending++;
      io.observe(el);
    });

    var countIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        countIo.unobserve(entry.target);
        countUp(entry.target);
      });
    }, { threshold: 0.6 });
    counters.forEach(function (el) { countIo.observe(el); });

    var zoomIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) entry.target.classList.add('is-in');
      });
    }, { threshold: 0.2 });
    zooms.forEach(function (el) { zoomIo.observe(el); });
  }

  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-ym-count'));
    var final = el.textContent;
    if (isNaN(target) || target <= 1) return;
    var duration = 1200;
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = p < 1 ? String(Math.max(1, Math.round(target * eased))) : final;
      if (p < 1) requestAnimationFrame(frame);
    }
    el.textContent = '1';
    requestAnimationFrame(frame);
  }

  function setupSticky() {
    var pill = document.querySelector('[data-ym-sticky]');
    if (!pill || !('IntersectionObserver' in window)) return;
    var watched = document.querySelectorAll('.ym-hero, #kaufen, footer');
    var visible = new Set();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) visible.add(e.target); else visible.delete(e.target);
      });
      var show = visible.size === 0;
      pill.classList.toggle('is-shown', show);
      pill.toggleAttribute('inert', !show);
    }, { threshold: 0 });
    watched.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Buy box: add to cart in place, then open the cart drawer ---------- */
  function setButton(button, state) {
    if (!button.dataset.label) button.dataset.label = button.textContent.trim();
    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-added', state === 'added');
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    if (state === 'added') button.textContent = button.dataset.added || 'Hinzugefügt';
    else if (state === 'error') button.textContent = button.dataset.label;
    else if (!state) button.textContent = button.dataset.label;
  }

  function flyToCart(form, button) {
    var image = form.getAttribute('data-ym-image');
    var icon = document.querySelector('.header-actions__cart-icon');
    if (reduce || !image || !icon) return;
    import('@theme/fly-to-cart').then(function () {
      var fly = document.createElement('fly-to-cart');
      fly.classList.add('fly-to-cart--main');
      fly.style.setProperty('background-image', 'url(' + image + ')');
      fly.style.setProperty('--start-opacity', '0');
      fly.source = button;
      fly.destination = icon;
      document.body.appendChild(fly);
    }).catch(function () {});
  }

  function ajaxAdd(form) {
    var button = form.querySelector('button[type="submit"]');
    var routes = window.Theme && window.Theme.routes;
    if (!button || !routes || !routes.cart_add_url) return false;
    if (button.classList.contains('is-loading')) return true;
    var errorNote = form.querySelector('.ym-buy__error');
    if (errorNote) errorNote.hidden = true;

    function fallback() {
      setButton(button, null);
      form.removeAttribute('data-ym-buy');
      form.submit();
    }

    setButton(button, 'loading');
    flyToCart(form, button);

    import('@shopify/events').then(function (events) {
      var LinesEvent = events && events.CartLinesUpdateEvent;
      if (!LinesEvent || typeof LinesEvent.createPromise !== 'function') { fallback(); return; }
      var data = new FormData(form);
      var ids = [];
      document.querySelectorAll('cart-items-component').forEach(function (c) {
        if (c.dataset.sectionId) ids.push(c.dataset.sectionId);
      });
      if (ids.length) data.append('sections', ids.join(','));
      var quantity = Number(data.get('quantity')) || 1;
      var deferred = LinesEvent.createPromise();

      form.dispatchEvent(new LinesEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(data.get('id')), quantity: quantity }],
        promise: deferred.promise
      }));

      return fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
        body: data
      })
        .then(function (r) { return r.json(); })
        .then(function (added) {
          return fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var failed = Boolean(added && added.status);
              deferred.resolve({
                cart: LinesEvent.createCartFromAjaxResponse(cart),
                detail: {
                  items: cart.items,
                  source: 'ym-buy',
                  sourceId: 'ym-buy',
                  itemCount: quantity,
                  sections: failed ? undefined : added.sections,
                  didError: failed
                }
              });
              if (failed) {
                setButton(button, 'error');
                if (errorNote) { errorNote.textContent = added.description || added.message || ''; errorNote.hidden = false; }
                return;
              }
              var drawer = document.getElementById('cart-drawer');
              if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) {
                setTimeout(function () { if (!drawer.isOpen) drawer.open(); }, 120);
              }
              setButton(button, 'added');
              setTimeout(function () { setButton(button, null); }, 2200);
            });
        })
        .catch(function (error) {
          deferred.reject(error);
          setButton(button, 'error');
        });
    }, fallback);
    return true;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('form[data-ym-buy]');
    if (!form) return;
    if (ajaxAdd(form)) event.preventDefault();
  });

  function init() {
    setup(document);
    setupSticky();
    document.addEventListener('scroll', queueSweep, { capture: true, passive: true });
    window.addEventListener('resize', queueSweep, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { setup(e.target); });
})();